import { v4 as uuid } from 'uuid';
import * as upath from 'upath';
import { FileStorageInterface } from './Interfaces/CWA/FileStorageInterface';
import { ObjectType } from './Interfaces/LSS/SingleMetadataV1';
import { LeaseSyncClientInterface } from './Interfaces/LSS/LeaseSyncClientInterface';
import { FilePathRequestV1 } from './Interfaces/LSS/FilePathRequestV1';
import { FilePathRequestV2 } from './Interfaces/LSS/FilePathRequestV2';
import { GetMetadataReplyV1 } from './Interfaces/LSS/GetMetadataReplyV1';
import { callSyncNative } from './NativeFunctions';
import { TaskContextInterface } from './Interfaces/CWA/TaskContextInterface';
import { Helper } from './Helper';
import { MetadataWithPath } from './Interfaces/MetadataWithPath';
import { SyncStatusInterface, SyncStatistics } from './Interfaces/SyncStatusInterface';
import { EndpointNotFoundError, HttpRequestError, NativeBindingError } from './Errors';
import { CLSYNC_FAIL, CLSYNC_PASS, GET_FILES, StatusCodes } from './Constants';
import { GetFileReplyV1 } from './Interfaces/LSS/GetFileReplyV1';
import { GetFileReplyV2 } from './Interfaces/LSS/GetFileReplyV2';

export class Worker {
	private fileStorage: FileStorageInterface;
	private leaseSyncClient: LeaseSyncClientInterface;
	private initialSyncTimeout: ReturnType<typeof setTimeout> | null;
	private periodicSyncInterval: ReturnType<typeof setInterval> | null;
	private taskContext: TaskContextInterface;
	private maxAcceptableErrorRate: number;
	private noOfApiCalls: number;
	private avgApiResponseTime: number;
	private filesDownloaded: Array<string>;
	private filesFailedToDownload: Array<string>;
	private filesDeleted: Array<string>;
	private backOffTimes: Array<number>;
	public abortController: AbortController;
	public constructor(
		fileStorage: FileStorageInterface,
		leaseSyncClient: LeaseSyncClientInterface,
		taskContext: TaskContextInterface,
		maxAcceptableErrorRate = 5,
		backOffTimes = [1 * 60 * 1000]
	) {
		this.fileStorage = fileStorage;
		this.leaseSyncClient = leaseSyncClient;
		this.taskContext = taskContext;
		this.leaseSyncClient.setContext(taskContext);
		this.initialSyncTimeout = null;
		this.periodicSyncInterval = null;
		this.maxAcceptableErrorRate = maxAcceptableErrorRate;
		this.backOffTimes = backOffTimes;
	}

	public startSync(): void {
		callSyncNative(
			'writeTraceMessage',
			`Starting CL Sync for customer ${this.taskContext.customerId}`,
			Date.now(),
			this.taskContext.userId,
			this.taskContext.storeGuid
		);

		this.abortController = new AbortController();
		const abortSignal = this.abortController.signal;
		//Invoke sync operation on invoking startSync
		this.syncLeaseFiles(abortSignal);

		//Invoke sync operation once after initial specified timeout
		if (this.taskContext.initialSyncDelay) {
			this.initialSyncTimeout = setTimeout(async () => {
				await this.syncLeaseFiles(abortSignal);
			}, this.taskContext.initialSyncDelay);
		}

		//Invoke sync operation periodically according to specified interval
		if (this.taskContext.syncFrequency) {
			this.periodicSyncInterval = setInterval(async () => {
				await this.syncLeaseFiles(abortSignal);
			}, this.taskContext.syncFrequency);
		}
	}

	public stopSync(): void {
		callSyncNative(
			'writeTraceMessage',
			`Stopping CL sync for Customer: ${this.taskContext.customerId}`,
			Date.now(),
			this.taskContext.userId,
			this.taskContext.storeGuid
		);
		this.abortController.abort();
		clearTimeout(this.initialSyncTimeout);
		clearInterval(this.periodicSyncInterval);
	}

	public setTaskContext(taskContext: TaskContextInterface): void {
		this.taskContext = taskContext;
		this.leaseSyncClient.setContext(taskContext);
	}
	/**
	 * @param  {number} ms
	 * @returns {Promise<void>}
	 * @summary wait for desired number of milli seconds
	 */
	public waitFor(ms: number): Promise<void> {
		return new Promise((resolve) => setTimeout(() => resolve(), ms));
	}

	/**
	 * @param  {FilePathRequestV1} request
	 * @param  {string} correlationId
	 * @returns {Promise<GetFileReplyV1>}
	 * @summary getFile API wrapper to get file from remote
	 * @description returns file if 200, null if 503 error, else throws
	 */
	public async getRemoteFile(
		request: FilePathRequestV1,
		correlationId: string
	): Promise<GetFileReplyV1> {
		let remoteFile = null;
		try {
			remoteFile = await this.leaseSyncClient.getFile(request, correlationId);
		} catch (error) {
			if (
				!(error instanceof HttpRequestError) ||
				error.statusCode != StatusCodes.ServiceUnavailable
			) {
				throw error;
			}
		}
		return remoteFile;
	}

	public async getRemoteFileV2(
		request: FilePathRequestV2,
		correlationId:string
	):Promise<GetFileReplyV2>{
		let remoteFiles =null;
		try{
			remoteFiles = await this.leaseSyncClient.getFiles(request, correlationId);
		}catch (error) {
			if (
				!(error instanceof HttpRequestError) ||
				error.statusCode != StatusCodes.ServiceUnavailable
			) {
				throw error;
			}
		}
		return remoteFiles;
	}

	public async saveLocalMetadata(
		path: string,
		metadata: GetMetadataReplyV1
	): Promise<void> {
		const isMetadataSaved = await this.fileStorage.saveMetadata(
			this.taskContext.userId,
			this.taskContext.storeGuid,
			path,
			metadata
		);
		if (!isMetadataSaved) {
			callSyncNative('writeErrorMessage', 'saveMetadata failed', Date.now());
		}
	}

	/**
	 * @param  {string} path
	 * @param  {GetMetadataReplyV1} childrenMetadata
	 * @returns {Promise<void>}
	 * @description gets all files in the given path that are obsolete and deletes them from local
	 */
	public async deleteObsoleteFiles(
		path: string,
		childrenMetadata: GetMetadataReplyV1
	): Promise<void> {
		const localFiles = await this.fileStorage.getAllFiles(
			this.taskContext.userId,
			this.taskContext.storeGuid,
			path
		);
		const filesToDelete = Helper.getFilesToDelete(localFiles, childrenMetadata);
		for (const ft of filesToDelete) {
			const fullPath = upath.join(path, ft);
			await this.removeRecursively(fullPath);
			this.filesDeleted.push(upath.basename(fullPath));
		}
	}

	/**
	 * @param  {Map<string, GetMetadataReplyV1> } filepathAndMetadata
	 * @param  {number} totalNoOfFiles
	 * @param  {string} correlationId
	 * @returns {Promise<Map<string, GetMetadataReplyV1>}
	 * @description downloads files using path & metadata, returns the failed ones
	 */
	public async downloadLeases(
		filesMetadata: Map<string, GetMetadataReplyV1>,
		totalNoOfFiles: number,
		correlationId: string
	): Promise<Map<string, GetMetadataReplyV1>> {
		let currentContinuousErrors = 0;
		let start = 0;
		let end = 0;
		const failedFilesMetadata = new Map<string, GetMetadataReplyV1>();
		const remoteMetadataArray = new Map<string, GetMetadataReplyV1>();
		
		if(this.taskContext.getFilesEnabled != null && this.taskContext.getFilesEnabled ==true && this.taskContext.endpoints[GET_FILES]){
			callSyncNative('writeTraceMessage', 'Getfiles endpoint enabled', Date.now());
			const request:FilePathRequestV2 = {
				customerId: this.taskContext.customerId,
			    deviceId: this.taskContext.deviceId,
			    fullPaths: [],
			}
			
			for(const [filePath,remoteMetadata] of filesMetadata){
				request.fullPaths.push(filePath)
				remoteMetadataArray.set(filePath,remoteMetadata);
			}
			
			const totalFilesToDownload = request.fullPaths.length;
			callSyncNative('writeTraceMessage','Total files to download: '+ totalFilesToDownload, Date.now());
			/* get file content from remote */
			start = Date.now();
			const remoteFiles = await this.getRemoteFileV2(request,correlationId);
			end = Date.now();
			this.avgApiResponseTime += end - start;
			this.noOfApiCalls++;
			
			if(remoteFiles == null){
				callSyncNative('writeTraceMessage', 'All files failed to download', Date.now());
				/* add allfiles to failed list so that we may retry it later on */
				for(const [filePath,remoteMetadata]of filesMetadata){
					failedFilesMetadata.set(filePath, remoteMetadata);
					this.filesFailedToDownload.push(upath.basename(filePath));
				}

			}
			else{
				const remoteFilesLength = remoteFiles.files.length;
				const remoteMetadataArrayToRetry = new Map(filesMetadata);
				if(totalFilesToDownload == remoteFilesLength){
					currentContinuousErrors = 0;
				}
				else{
					currentContinuousErrors = totalFilesToDownload-remoteFilesLength;
					callSyncNative('writeTraceMessage', currentContinuousErrors + 'files failed to download pushing them failed metadata', Date.now());
					for(const file of remoteFiles.files){
						const filepathIndex =  request.fullPaths.findIndex((ele)=>ele.includes(file.metadata.name))	
						if(filepathIndex != -1){
							remoteMetadataArrayToRetry.delete(request.fullPaths[filepathIndex])
						}
					}

					for(const [filePath,remoteMetadata]of remoteMetadataArrayToRetry){
						failedFilesMetadata.set(filePath, remoteMetadata);
						this.filesFailedToDownload.push(upath.basename(filePath));
					}
				}
		
				/* if continuous errors become more than the max acceptable rate we stop the cycle */
				if (currentContinuousErrors / totalNoOfFiles > this.maxAcceptableErrorRate / 100) {
						return;
				}

				for(const file of remoteFiles.files){
				/* if we were able to get file content, save it to local */
				if(file != null && file.content){
					const filepathIndex = request.fullPaths.findIndex((ele)=>ele.includes(file.metadata.name))	
					if(filepathIndex != -1){
						const remoteMetadata = remoteMetadataArray.get(request.fullPaths[filepathIndex]);
						const isFileSaved = await this.fileStorage.saveFile(
							this.taskContext.userId,
							this.taskContext.storeGuid,
							request.fullPaths[filepathIndex],
							file.content
						);
						 /* if file was successfully saved to local, save its metadata
						 * else mark the current file as failed to download
						 */
						if (isFileSaved) {
							this.filesDownloaded.push(upath.basename(request.fullPaths[filepathIndex]));
							await this.saveLocalMetadata(request.fullPaths[filepathIndex], remoteMetadata);
						} else {
							this.filesFailedToDownload.push(upath.basename(request.fullPaths[filepathIndex]));
						}
					}
					else{
						this.filesFailedToDownload.push(upath.basename(request.fullPaths[filepathIndex]));
					}
				}

					
			}
			}
			
		}
		else{
			const request: FilePathRequestV1 = {
				customerId: this.taskContext.customerId,
				deviceId: this.taskContext.deviceId,
				fullPath: '',
			};
			for (const [filePath, remoteMetadata] of filesMetadata) {
				let fileDownloaded = true;
	
				/* get file content from remote */
				start = Date.now();
				request.fullPath = filePath;
				const remoteFile = await this.getRemoteFile(request, correlationId);
				if (remoteFile == null) {
					currentContinuousErrors++;
					fileDownloaded = false;
					/* add current file to failed list so that we may retry it later on */
					failedFilesMetadata.set(filePath, remoteMetadata);
					this.filesFailedToDownload.push(upath.basename(filePath));
				} else {
					currentContinuousErrors = 0;
				}
				end = Date.now();
				this.avgApiResponseTime += end - start;
				this.noOfApiCalls++;
	
				/* if continuous errors become more than the max acceptable rate we stop the cycle */
				if (currentContinuousErrors / totalNoOfFiles > this.maxAcceptableErrorRate / 100) {
					break;
				}
	
				/* if we were able to get file content, save it to local */
				if (fileDownloaded && remoteFile != null && remoteFile.content != null) {
					const isFileSaved = await this.fileStorage.saveFile(
						this.taskContext.userId,
						this.taskContext.storeGuid,
						filePath,
						remoteFile.content
					);
					/**
					 * if file was successfully saved to local, save its metadata
					 * else mark the current file as failed to download
					 */
					if (isFileSaved) {
						this.filesDownloaded.push(upath.basename(filePath));
						await this.saveLocalMetadata(filePath, remoteMetadata);
					} else {
						this.filesFailedToDownload.push(upath.basename(filePath));
					}
				}
			}
		}
		
		return failedFilesMetadata;
	}

	public async retryLeaseSync(
		failedFilesMetadata: Map<string, GetMetadataReplyV1>,
		totalNoOfFiles: number,
		correlationId: string
	): Promise<void> {
		for (const backOffTime of this.backOffTimes) {
			if (failedFilesMetadata.size / totalNoOfFiles > this.maxAcceptableErrorRate / 100) {
				throw new Error('exceeded acceptable error rate');
			}
			if (failedFilesMetadata.size > 0) {
				/* wait for <backOffTime> ms and download files that failed earlier */
				await this.waitFor(backOffTime);
				failedFilesMetadata = await this.downloadLeases(
					failedFilesMetadata,
					totalNoOfFiles,
					correlationId
				);
			}
		}
	}

	public onSyncAbort(): void {
		callSyncNative(
			'writeTraceMessage',
			'aborting lease file sync',
			Date.now(),
			this.taskContext.userId,
			this.taskContext.storeGuid
		);
	}

	public async syncLeaseFilesUtil(correlationId: string): Promise<number> {
		let start = 0;
		let end = 0;
		let totalNoOfFiles = 0;
		start = Date.now();
		const rootPath = '';
		const request: FilePathRequestV1 = {
			customerId: this.taskContext.customerId,
			deviceId: this.taskContext.deviceId,
			fullPath: rootPath,
		};
		const rootMetadata = await this.leaseSyncClient.getRootMetadata(
			request,
			correlationId
		);
		end = Date.now();
		this.avgApiResponseTime += end - start;
		this.noOfApiCalls++;

		const current = {
			path: rootPath,
			metadata: rootMetadata,
		};

		const syncQueue = Array<MetadataWithPath>();
		syncQueue.push(current);
		const filesMetadata = new Map<string, GetMetadataReplyV1>();
		/**
		 * start sync queue with root folder ""
		 * keep adding subsequent files/folder to queue
		 */
		while (syncQueue.length !== 0) {
			const { metadata: remoteMetadata, path } = syncQueue.pop();
			let modified = false;
			const localMetadata = await this.fileStorage.getMetadata(
				this.taskContext.userId,
				this.taskContext.storeGuid,
				path
			);

			if (!Helper.isValidMetadataReply(remoteMetadata)) {
				continue;
			}
			if (!Helper.compareLastModified(localMetadata, remoteMetadata)) {
				modified = true;
			}

			for (const md of remoteMetadata.metadataArray) {
				if (md.type === ObjectType.FOLDER) {
					request.fullPath = path;
					start = Date.now();
					const childrenMetadata = await this.leaseSyncClient.getChildrenMetadata(
						request,
						correlationId
					);
					end = Date.now();
					this.avgApiResponseTime += end - start;
					this.noOfApiCalls++;

					if (modified) {
						await this.deleteObsoleteFiles(path, childrenMetadata);
					}

					if (Helper.isValidMetadataReply(childrenMetadata)) {
						/* push files of current folder to queue */
						for (const childMetadata of childrenMetadata.metadataArray) {
							const childPath = upath.join(path, childMetadata.name);
							if (childMetadata.type === ObjectType.FILE) {
								totalNoOfFiles++;
							}
							const temp: GetMetadataReplyV1 = {
								metadataArray: [childMetadata],
							};
							syncQueue.push({
								metadata: temp,
								path: childPath,
							});
						}
					}
					/* save folder metadata */
					await this.saveLocalMetadata(path, remoteMetadata);
				} else {
					if (modified) {
						filesMetadata.set(path, remoteMetadata);
					} else {
						/* remove this file from the count because we needn't download it */
						totalNoOfFiles--;
					}
				}
			}
		}

		/* download key, lease files from above metadata responses */
		const failedFilesMetadata = await this.downloadLeases(
			filesMetadata,
			totalNoOfFiles,
			correlationId
		);

		/**
		 * retry downloading files that failed earlier
		 * following a backoff dictated by backOffTimes
		 */
		await this.retryLeaseSync(failedFilesMetadata, totalNoOfFiles, correlationId);
		return totalNoOfFiles;
	}

	public async syncLeaseFiles(abortSignal: AbortSignal): Promise<void> {
		//Create/reset counters for measuring statistics
		let syncTime = 0;
		this.filesDeleted = [];
		this.filesFailedToDownload = [];
		this.filesDownloaded = [];
		this.avgApiResponseTime = 0;
		this.noOfApiCalls = 0;

		let totalNoOfFiles = 0; // total no. of files that need to be downloaded in current sync

		abortSignal.addEventListener('abort', () => {
			this.onSyncAbort();
			return;
		});

		const rootPath = '';

		syncTime = Date.now();
		const correlationId = uuid();
		callSyncNative(
			'writeTraceMessage',
			`CL Sync cycle with CorrelationID: ${correlationId} for customer: ${this.taskContext.customerId}`,
			Date.now(),
			this.taskContext.userId,
			this.taskContext.storeGuid
		);
		let reason = '';
		let syncStatus = CLSYNC_PASS;
		let statusCode = StatusCodes.OK;
		try {
			/**
			 * Get root metadata with empty string as path
			 * Check if this metadata exists already
			 */
			let localRootMetadata = null;
			let remoteRootMetadata = null;
			do {
				totalNoOfFiles += await this.syncLeaseFilesUtil(correlationId);
				localRootMetadata = await this.fileStorage.getMetadata(
					this.taskContext.userId,
					this.taskContext.storeGuid,
					rootPath
				);
				const request: FilePathRequestV1 = {
					customerId: this.taskContext.customerId,
					deviceId: this.taskContext.deviceId,
					fullPath: rootPath,
				};
				remoteRootMetadata = await this.leaseSyncClient.getRootMetadata(
					request,
					correlationId
				);
			} while (!Helper.compareLastModified(localRootMetadata, remoteRootMetadata));
		} catch (e) {
			syncStatus = CLSYNC_FAIL;
			statusCode = StatusCodes.UnknownError;
			if (e instanceof HttpRequestError || e instanceof NativeBindingError ||e instanceof EndpointNotFoundError) {
				statusCode = e.statusCode;
			}
			callSyncNative('writeErrorMessage', e, Date.now());
			reason = `Exception in CL Sync: LLT support: ${
				this.taskContext.longLivedTokenSupport
			},
			${(e as Error).message}`;
			const CLSyncErrorMessage = `Exception in CL Sync:	${
				(e as Error).message
			}, LLT support: ${this.taskContext.longLivedTokenSupport}, stacktrace: ${
				(e as Error).stack
			}`;
			callSyncNative('writeErrorMessage', CLSyncErrorMessage, Date.now());
		}
		syncTime = Date.now() - syncTime;
		const syncStats: SyncStatistics = {
			totalNoOfFiles: totalNoOfFiles,
			filesDownloaded: this.filesDownloaded,
			filesFailedToDownload: this.filesFailedToDownload,
			filesDeleted: this.filesDeleted,
			avgAPIResponseTime: this.avgApiResponseTime / Math.max(1, this.noOfApiCalls),
			syncTimeTaken: syncTime,
		};
		const status: SyncStatusInterface = {
			customerId: this.taskContext.customerId,
			userId: this.taskContext.userId,
			storeGuid: this.taskContext.storeGuid,
			syncCorrelationId: correlationId,
			syncStatus: syncStatus,
			reason: reason,
			syncStats: syncStats,
			timestamp: Date.now(),
			statusCode: statusCode,
		};
		callSyncNative('notifyCLSyncStatus', status);
	}

	/**
	 * Deletes the item along with all its contents/children and metadata
	 *
	 * @param fullPath - The relative path of the item to delete
	 */
	public async removeRecursively(fullPath: string): Promise<void> {
		//Get metadata of the associated item
		const metadata = await this.fileStorage.getMetadata(
			this.taskContext.userId,
			this.taskContext.storeGuid,
			fullPath
		);
		if (!Helper.isValidMetadataReply(metadata)) {
			return;
		}
		if (metadata.metadataArray[0].type === ObjectType.FILE) {
			const isFileDeleted = await this.fileStorage.deleteFile(
				this.taskContext.userId,
				this.taskContext.storeGuid,
				fullPath
			);
			if (isFileDeleted) {
				const isMetadataDeleted = await this.fileStorage.deleteMetadata(
					this.taskContext.userId,
					this.taskContext.storeGuid,
					fullPath
				);
				if (!isMetadataDeleted) {
					callSyncNative('writeErrorMessage', 'deleteMetadata failed', Date.now());
				}
			}
		} else if (metadata.metadataArray[0].type === ObjectType.FOLDER) {
			const filesInFolder = await this.fileStorage.getAllFiles(
				this.taskContext.userId,
				this.taskContext.storeGuid,
				fullPath
			);
			for (const file of filesInFolder) {
				await this.removeRecursively(upath.join(fullPath, file));
			}
			const isFolderDeleted = await this.fileStorage.deleteFolder(
				this.taskContext.userId,
				this.taskContext.storeGuid,
				fullPath
			);
			if (isFolderDeleted) {
				const isMetadataDeleted = await this.fileStorage.deleteMetadata(
					this.taskContext.userId,
					this.taskContext.storeGuid,
					fullPath
				);
				if (!isMetadataDeleted) {
					callSyncNative('writeErrorMessage', 'deleteMetadata failed', Date.now());
				}
			}
		}
	}
}
